import React from "react";
import { Box, Typography, Grid, Chip, Avatar } from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const BBPS_ITEMS = [
  { label: "Mobile Prepaid", icon: "📱", path: "/dashboard/bbps/mobileprepaid", color: "#16a34a", bg: "#f0fdf4" },
  { label: "Electricity", icon: "⚡", path: "/dashboard/bbps/Electricity", color: "#f59e0b", bg: "#fffbeb" },
  { label: "DTH", icon: "📡", path: "/dashboard/bbps/DTH", color: "#0ea5e9", bg: "#f0f9ff" },
  { label: "FASTag", icon: "🚗", path: "/dashboard/bbps/Fastag", color: "#7c3aed", bg: "#faf5ff" },
  { label: "Water Bill", icon: "💧", path: "/dashboard/bbps/Water", color: "#06b6d4", bg: "#ecfeff" },
  { label: "Gas", icon: "🔥", path: "/dashboard/bbps/gas", color: "#dc2626", bg: "#fff1f2" },
  { label: "LPG Gas", icon: "🏮", path: "/dashboard/bbps/LPGGas", color: "#ea580c", bg: "#fff7ed" },
  { label: "Broadband", icon: "🌐", path: "/dashboard/bbps/Broadbandpostpaid", color: "#2563eb", bg: "#eff6ff" },
  { label: "Cable TV", icon: "📺", path: "/dashboard/bbps/Cabletv", color: "#9333ea", bg: "#faf5ff" },
  { label: "Landline", icon: "📞", path: "/dashboard/bbps/Landlinepostpaid", color: "#0891b2", bg: "#ecfeff" },
  { label: "Insurance", icon: "🛡️", path: "/dashboard/bbps/Insurance", color: "#16a34a", bg: "#f0fdf4" },
  { label: "Loan EMI", icon: "🏠", path: "/dashboard/bbps/LoanRepayment", color: "#f97316", bg: "#fff7ed" },
];

const HOW_STEPS = [
  { num: "01", title: "Create Account", desc: "Register with your mobile number in 2 minutes. No paperwork needed.", icon: "👤" },
  { num: "02", title: "Add Money", desc: "Load your wallet instantly from any bank account, debit or credit card.", icon: "💳" },
  { num: "03", title: "Pay & Transact", desc: "Pay bills, send money, recharge — anytime, anywhere, in seconds.", icon: "⚡" },
];

export default function BBPSSection() {
  const navigate = useNavigate();

  return (
    <>
      {/* BBPS Grid */}
      <Box id="wallet" sx={{ py: { xs: 8, md: 12 }, px: { xs: 3, md: 8, lg: 12 }, bgcolor: "white" }}>
        <Box sx={{ textAlign: "center", mb: { xs: 5, md: 7 } }}>
          <Chip label="BBPS Certified" sx={{ bgcolor: "#dcfce7", color: "#15803d", fontWeight: 700, fontSize: 12, mb: 2 }} />
          <Typography sx={{ fontSize: { xs: 28, md: 40 }, fontWeight: 900, color: "#0f172a", letterSpacing: "-1px", lineHeight: 1.15, mb: 1.5 }}>
            Pay any bill,<br />right now
          </Typography>
          <Typography sx={{ fontSize: { xs: 15, md: 17 }, color: "#64748b" }}>
            100+ BBPS billers. Instant confirmation. Zero late fees.
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {BBPS_ITEMS.map((item, i) => (
            <Grid size={{xs:4, md:2, sm:3}} item xs={4} sm={3} md={2} key={i}>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: i * 0.04 }}
              >
                <Box
                  onClick={() => navigate(item.path)}
                  sx={{
                    display: "flex", flexDirection: "column", alignItems: "center",
                    gap: 1, p: { xs: 1.5, md: 2 }, borderRadius: "16px",
                    bgcolor: "white", border: "1.5px solid #f1f5f9",
                    cursor: "pointer", transition: "all 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      borderColor: item.color,
                      bgcolor: item.bg,
                      boxShadow: `0 8px 24px ${item.color}20`,
                    },
                  }}
                >
                  <Box sx={{
                    width: { xs: 44, md: 52 }, height: { xs: 44, md: 52 },
                    borderRadius: "14px", bgcolor: item.bg,
                    border: `1.5px solid ${item.color}30`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: { xs: 22, md: 26 },
                  }}>
                    {item.icon}
                  </Box>
                  <Typography sx={{
                    fontSize: { xs: 10, md: 12 }, fontWeight: 600,
                    color: "#374151", textAlign: "center", lineHeight: 1.3,
                  }}>
                    {item.label}
                  </Typography>
                </Box>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* How It Works */}
      <Box id="about" sx={{ py: { xs: 8, md: 12 }, px: { xs: 3, md: 8, lg: 12 }, bgcolor: "#0f172a", position: "relative", overflow: "hidden" }}>
        <Box sx={{ position: "absolute", top: "10%", right: "5%", width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle, rgba(22,163,74,0.15) 0%, transparent 70%)", pointerEvents: "none" }} />
        <Box sx={{ position: "absolute", bottom: "10%", left: "2%", width: 200, height: 200, borderRadius: "50%", background: "radial-gradient(circle, rgba(14,165,233,0.12) 0%, transparent 70%)", pointerEvents: "none" }} />

        <Box sx={{ textAlign: "center", mb: { xs: 6, md: 8 }, position: "relative", zIndex: 1 }}>
          <Chip label="Simple & Fast" sx={{ bgcolor: "rgba(22,163,74,0.15)", color: "#4ade80", fontWeight: 700, fontSize: 12, mb: 2, border: "1px solid rgba(22,163,74,0.3)" }} />
          <Typography sx={{ fontSize: { xs: 28, md: 40 }, fontWeight: 900, color: "white", letterSpacing: "-1px", lineHeight: 1.15, mb: 1.5 }}>
            Up and running in
            <Box component="span" sx={{ display: "block", background: "linear-gradient(135deg, #16a34a, #4ade80)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              under 2 minutes
            </Box>
          </Typography>
        </Box>

        <Grid container spacing={4} sx={{ position: "relative", zIndex: 1 }}>
          {HOW_STEPS.map((step, i) => (
            <Grid size={{xs:12, md:4}} item xs={12} md={4} key={i}>
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.15 }}>
                <Box sx={{ textAlign: "center", px: { md: 2 } }}>
                  <Box sx={{ width: 72, height: 72, borderRadius: "20px", background: "linear-gradient(135deg, rgba(22,163,74,0.2), rgba(34,197,94,0.1))", border: "1.5px solid rgba(22,163,74,0.3)", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2.5, fontSize: 32 }}>
                    {step.icon}
                  </Box>
                  <Typography sx={{ fontSize: 12, color: "#4ade80", fontWeight: 700, letterSpacing: "2px", mb: 1 }}>STEP {step.num}</Typography>
                  <Typography sx={{ fontSize: { xs: 20, md: 22 }, fontWeight: 800, color: "white", mb: 1.5 }}>{step.title}</Typography>
                  <Typography sx={{ fontSize: 15, color: "#94a3b8", lineHeight: 1.75 }}>{step.desc}</Typography>
                </Box>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* CTA Strip */}
        <Box sx={{
          mt: { xs: 8, md: 10 }, p: { xs: 4, md: 6 },
          borderRadius: "24px",
          background: "linear-gradient(135deg, rgba(22,163,74,0.15) 0%, rgba(14,165,233,0.10) 100%)",
          border: "1px solid rgba(22,163,74,0.25)",
          textAlign: "center", position: "relative", zIndex: 1,
        }}>
          <Typography sx={{ fontSize: { xs: 24, md: 32 }, fontWeight: 900, color: "white", mb: 1.5, letterSpacing: "-0.5px" }}>
            Ready to experience the future of payments?
          </Typography>
          <Typography sx={{ fontSize: 16, color: "#94a3b8", mb: 4 }}>
            Join 10M+ Indians who trust TA Wallet for their daily transactions.
          </Typography>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
            <Box sx={{ cursor: "pointer" }} onClick={() => window.open("https://play.google.com/store/apps/details?id=com.ta.wallet.tawallet", "_blank")}>
              <Box component="img" src="/Assets/Googleplay.png" sx={{ height: 44, borderRadius: 1 }} onError={(e)=>{e.target.style.display="none";}} />
            </Box>
            <Box sx={{ cursor: "pointer" }} onClick={() => window.open("https://apps.apple.com/in/app/ta-wallet/id1381087275", "_blank")}>
              <Box component="img" src="/Assets/Playstore.png" sx={{ height: 44, borderRadius: 1 }} onError={(e)=>{e.target.style.display="none";}} />
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}
